import { AxiosMode } from '@/axiosModel';
import type { StoryItem } from '@/types';

export interface StoryListParams {
  current?: number;
  size?: number;
  keyword?: string;
}

export interface StoryListResult {
  list: StoryItem[];
  total: number;
  current: number;
  size: number;
}

interface BackendStoryListResult {
  lists?: BackendStoryRecord[];
  list?: BackendStoryRecord[];
  listCount?: number;
  allCount?: number;
  current?: number;
  size?: number;
}

interface BackendStoryRecord {
  id?: string | number;
  title?: string;
  thumbnailUrl?: string;
  coverUrl?: string;
  url?: string;
  webUrl?: string;
  domainUrl?: string;
  newTime?: string | number;
  updateTime?: string | number;
  createdAt?: string | number;
  updatedAt?: string | number;
  bStatus?: string | number;
  auditStatus?: string | number;
  auditOpinion?: string;
  generateProgress?: string | number | null;
  latest_task_id?: string | number;
}

const apis = new AxiosMode();
const STORY_FOLDER_API_BASE = `${AxiosMode.API_HOST}/api/folder/aipicturebook`;
const AIPICTUREBOOK_API_BASE = `${AxiosMode.API_HOST}/api/aipicturebook`;

export const STORY_PAGE_SIZE = 20;

export async function fetchStoryList(params: StoryListParams = {}): Promise<StoryListResult> {
  const current = params.current ?? 1;
  const size = params.size ?? STORY_PAGE_SIZE;
  const { req } = apis.post({
    postData: removeEmptyFields({
      current,
      size,
      keyword: params.keyword?.trim()
    }),
    remotePath: `${STORY_FOLDER_API_BASE}/list-folder-data`
  });
  const res = (await req) as { data?: BackendStoryListResult };
  const data = res.data ?? {};
  const records = data.lists ?? data.list ?? [];

  return {
    list: records.map(mapBackendStory),
    total: Number(data.listCount ?? data.allCount ?? records.length) || 0,
    current: Number(data.current ?? current) || current,
    size: Number(data.size ?? size) || size
  };
}

export async function deleteStory(id: string | number): Promise<void> {
  const { req } = apis.post({
    postData: { datas: String(id) },
    remotePath: `${STORY_FOLDER_API_BASE}/delete-folder-data`
  });

  await req;
}

export async function updateStoryTitle(id: string | number, title: string): Promise<void> {
  const { req } = apis.post({
    postData: { id, title },
    remotePath: `${AIPICTUREBOOK_API_BASE}/update`
  });

  await req;
}

function mapBackendStory(data: BackendStoryRecord): StoryItem {
  const createdAt = formatApiTime(data.newTime ?? data.createdAt);
  const updatedAt = formatApiTime(data.updateTime ?? data.updatedAt) || createdAt;

  return {
    id: toStringValue(data.id),
    title: toStringValue(data.title) || '未命名故事',
    coverUrl: normalizeUrl(data.thumbnailUrl || data.coverUrl),
    createdAt,
    updatedAt,
    url: normalizeUrl(data.url || data.webUrl || data.domainUrl),
    bStatus: toNumberValue(data.bStatus, 1),
    auditStatus: toNumberValue(data.auditStatus, 0),
    auditOpinion: toStringValue(data.auditOpinion),
    generateProgress: getGenerateProgress(data),
    latestTaskId: toStringValue(data.latest_task_id),
    isReading: false
  };
}

function getGenerateProgress(data: BackendStoryRecord) {
  const progress = toNumberValue(data.generateProgress, Number.NaN);
  if (Number.isFinite(progress)) return Math.max(0, Math.min(100, progress));

  return toNumberValue(data.bStatus, 1) === -5 ? 65 : null;
}

function formatApiTime(value: unknown) {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'string' && Number.isNaN(Number(value))) return value;

  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return '';

  const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  const date = new Date(normalizedTimestamp);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function normalizeUrl(value: unknown) {
  const url = toStringValue(value).trim();
  if (!url) return '';
  if (url.startsWith('//')) return `https:${url}`;
  return url;
}

function toStringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function toNumberValue(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return fallback;
}

function pad(value: number) {
  return String(value).padStart(2, '0');
}

function removeEmptyFields(payload: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  );
}
